import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import {
  INSTANCES_TABLE_HEADERS,
  VIEW_INSTANCE_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  transformInstanceResponseRow,
  transformInstanceRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import ViewRow from "../components/dataTable/ViewRow";
import { getInstanceByRef } from "../api";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { getPageTranslationText } from "../translations/util";
import { Lang, Operation, Page } from "../translations/types";

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.INSTANCE,
  op: Operation.READ,
});

const Instances = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  useAppTitle("Instances - IWF Config Admin Tool (ICAT) by AT&T");
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [instancesData, setInstancesData] = useState<any[]>([]);

  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
  });

  const fetchInstances = async () => {
    setLoading(true);
    try {
      const { pageNum, pageSize } = searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/instances?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setInstancesData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={<div className="dropdown-details">{null}</div>}
      />

      {showSidePanel && selectedConnectionId ? (
        <ViewRow
          panelName={translations.viewRow.panelName}
          org={selectedConnectionId}
          esRef={selectedConnectionId}
          setShowEditPanel={handleToggleSidePanel}
          formFields={VIEW_INSTANCE_FORM_FIELDS}
          transformResponseRow={transformInstanceResponseRow}
          loadDataByRef={getInstanceByRef}
        />
      ) : null}

      <DataTableWithControls
        loading={loading}
        showTableControls={false}
        filterColumns={[]}
        selectedColumn={null}
        setSelectedColumn={() => null}
        onSearch={() => fetchInstances()}
        showCreateButton={false}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows={transformInstanceRows(instancesData).map((row: any) => ({
          ...row,
          action: (
            <TableActionButton
              onActionButtonClick={() => {
                setSelectedConnectionId(row.id);
                handleToggleSidePanel();
              }}
            />
          ),
        }))}
        headers={INSTANCES_TABLE_HEADERS}
        pageNum={searchQuery.pageNum}
        setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
        totalItems={numberOfRows}
        totalPages={numberOfPages}
        pageSize={searchQuery.pageSize}
        setPageSize={(pageSize) => setSearhQuery({ ...searchQuery, pageSize })}
      />
    </>
  );
};

export default Instances;
